import React, { useState } from 'react';

const ExportPDFModal = ({ onClose, onExport, t = {} }) => {
  const [density, setDensity] = useState('balanced');
  const [layoutStyle, setLayoutStyle] = useState('editorial');
  const [orientation, setOrientation] = useState('portrait');
  const [exportContent, setExportContent] = useState('all');
  const [includeUnrated, setIncludeUnrated] = useState(false);
  const [showComments, setShowComments] = useState(true);
  const [showReactions, setShowReactions] = useState(true);
  const [showMetadata, setShowMetadata] = useState(true);
  const [includeDocuments, setIncludeDocuments] = useState(true);
  const [includeNotes, setIncludeNotes] = useState(false);
  const [includeFinalSelections, setIncludeFinalSelections] = useState(true);

  const handleExport = () => {
    onExport({
      density: density,
      layout_style: layoutStyle,
      orientation: orientation,
      export_content: exportContent,
      include_unrated: includeUnrated,
      show_comments: showComments,
      show_reactions: showReactions,
      show_metadata: showMetadata,
      include_documents: includeDocuments,
      include_notes: includeNotes,
      include_final_selections: includeFinalSelections
    });
  };

  const densityOptions = [
    {
      value: 'spacious',
      label: t.density_spacious || 'Spacious',
      sub: t.density_spacious_sub || 'Approx. 1–2 images per page',
      description: t.density_spacious_desc || 'Elegant presentation with large spacing',
    },
    {
      value: 'balanced',
      label: t.density_balanced || 'Balanced',
      sub: t.density_balanced_sub || 'Approx. 2–4 images per page',
      description: t.density_balanced_desc || 'Clean gallery-style layout',
    },
    {
      value: 'compact',
      label: t.density_compact || 'Compact',
      sub: t.density_compact_sub || 'Approx. 6–12 images per page',
      description: t.density_compact_desc || 'Adaptive overview grid for quick client review',
    },
  ];

  const layoutOptions = [
    { value: 'gallery', label: t.layout_gallery || 'Gallery', description: t.layout_gallery_desc || 'Clean, minimal, artwork-focused' },
    { value: 'editorial', label: t.layout_editorial || 'Editorial', description: t.layout_editorial_desc || 'Elegant with labels and reactions' },
    { value: 'reference', label: t.layout_reference || 'Reference', description: t.layout_reference_desc || 'Compact with full details' },
  ];

  return (
    <>
      <div className="modal-backdrop" onClick={onClose}></div>
      <div className="modal-content fade-in" style={{ maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto' }}>
        <h2 style={{ 
          fontFamily: 'Playfair Display, serif', 
          fontSize: '24px', 
          marginBottom: '8px',
          textAlign: 'center'
        }}>
          {t.export_to_pdf_title || 'Export to PDF'}
        </h2>
        <p style={{ 
          textAlign: 'center', 
          color: 'var(--kahla-text-light)', 
          fontSize: '13px',
          marginBottom: '32px'
        }}>
          {t.export_preferences_subtitle || 'Choose your export preferences'}
        </p>

        {/* Export Content Selection */}
        <div style={{ marginBottom: '28px' }}>
          <label style={{ 
            display: 'block',
            fontSize: '11px',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'var(--kahla-text-light)',
            marginBottom: '12px'
          }}>
            {t.export_content_label || 'Export Content'}
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label 
              style={{ 
                display: 'flex',
                alignItems: 'flex-start',
                padding: '12px 16px',
                border: exportContent === 'all' 
                  ? '2px solid var(--kahla-charcoal)' 
                  : '1px solid var(--kahla-border)',
                backgroundColor: exportContent === 'all' 
                  ? 'var(--kahla-cream-light)' 
                  : 'var(--kahla-white)',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              <input 
                type="radio"
                name="exportContent"
                value="all"
                checked={exportContent === 'all'}
                onChange={() => setExportContent('all')}
                style={{ marginRight: '12px', marginTop: '2px' }}
              />
              <div>
                <p style={{ fontSize: '14px', fontWeight: '500', marginBottom: '2px' }}>
                  {t.all_images_option || 'All images'}
                </p>
                <p style={{ fontSize: '11px', color: 'var(--kahla-text-light)' }}>
                  {t.export_entire_board || 'Export the entire board'}
                </p>
              </div>
            </label>
            <label 
              style={{ 
                display: 'flex',
                alignItems: 'flex-start',
                padding: '12px 16px',
                border: exportContent === 'client_selected' 
                  ? '2px solid var(--kahla-charcoal)' 
                  : '1px solid var(--kahla-border)',
                backgroundColor: exportContent === 'client_selected' 
                  ? 'var(--kahla-cream-light)' 
                  : 'var(--kahla-white)',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              <input 
                type="radio"
                name="exportContent"
                value="client_selected"
                checked={exportContent === 'client_selected'}
                onChange={() => setExportContent('client_selected')}
                style={{ marginRight: '12px', marginTop: '2px' }}
              />
              <div>
                <p style={{ fontSize: '14px', fontWeight: '500', marginBottom: '2px' }}>
                  {t.client_selected_only || 'Client-selected only'}
                </p>
                <p style={{ fontSize: '11px', color: 'var(--kahla-text-light)' }}>
                  {t.client_selected_desc || 'Only ❤️ "I feel this" and 🤍 "Neutral" · Excludes ❌ "Not me"'}
                </p>
              </div>
            </label>
          </div>
          
          {exportContent === 'client_selected' && (
            <label style={{ 
              display: 'flex',
              alignItems: 'center',
              padding: '10px 16px',
              marginTop: '8px',
              backgroundColor: 'var(--kahla-cream-light)',
              cursor: 'pointer',
              fontSize: '13px'
            }}>
              <input 
                type="checkbox"
                checked={includeUnrated}
                onChange={(e) => setIncludeUnrated(e.target.checked)}
                style={{ marginRight: '10px' }}
              />
              {t.include_unrated_images || 'Include unrated images'}
            </label>
          )}
        </div>

        {/* Image Density */}
        <div style={{ marginBottom: '28px' }}>
          <label style={{
            display: 'block',
            fontSize: '11px',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'var(--kahla-text-light)',
            marginBottom: '12px'
          }}>
            {t.image_density_label || 'PDF Density'}
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {densityOptions.map((option) => (
              <label
                key={option.value}
                data-testid={`density-${option.value}`}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  padding: '14px 16px',
                  border: density === option.value
                    ? '2px solid var(--kahla-charcoal)'
                    : '1px solid var(--kahla-border)',
                  backgroundColor: density === option.value
                    ? 'var(--kahla-cream-light)'
                    : 'var(--kahla-white)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <input
                  type="radio"
                  name="density"
                  value={option.value}
                  checked={density === option.value}
                  onChange={() => setDensity(option.value)}
                  style={{ marginRight: '12px', marginTop: '3px' }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: '15px', fontWeight: '500', margin: 0, fontFamily: 'Playfair Display, serif', letterSpacing: '0.01em', color: 'var(--kahla-charcoal)' }}>
                    {option.label}
                  </p>
                  <p style={{ fontSize: '11px', color: 'var(--kahla-text-light)', margin: '3px 0 0', fontStyle: 'italic', letterSpacing: '0.02em' }}>
                    {option.sub}
                  </p>
                  <p style={{ fontSize: '12px', color: 'var(--kahla-text)', margin: '6px 0 0', lineHeight: 1.5 }}>
                    {option.description}
                  </p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Layout Style */}
        <div style={{ marginBottom: '28px' }}>
          <label style={{ 
            display: 'block',
            fontSize: '11px',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'var(--kahla-text-light)',
            marginBottom: '12px'
          }}>
            {t.layout_style_label || 'Layout Style'}
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            {layoutOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setLayoutStyle(option.value)}
                style={{ 
                  flex: 1,
                  padding: '12px 8px',
                  border: layoutStyle === option.value 
                    ? '2px solid var(--kahla-charcoal)' 
                    : '1px solid var(--kahla-border)',
                  backgroundColor: layoutStyle === option.value 
                    ? 'var(--kahla-cream-light)' 
                    : 'var(--kahla-white)',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.2s ease'
                }}
              >
                <p style={{ fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                  {option.label}
                </p>
                <p style={{ fontSize: '10px', color: 'var(--kahla-text-light)' }}>
                  {option.description}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Orientation */}
        <div style={{ marginBottom: '32px' }}>
          <label style={{ 
            display: 'block',
            fontSize: '11px',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'var(--kahla-text-light)',
            marginBottom: '12px'
          }}>
            {t.page_orientation_label || 'Page Orientation'}
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setOrientation('portrait')}
              style={{ 
                flex: 1,
                padding: '16px',
                border: orientation === 'portrait' 
                  ? '2px solid var(--kahla-charcoal)' 
                  : '1px solid var(--kahla-border)',
                backgroundColor: orientation === 'portrait' 
                  ? 'var(--kahla-cream-light)' 
                  : 'var(--kahla-white)',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease'
              }}
            >
              <div style={{ 
                width: '24px', 
                height: '32px', 
                border: '2px solid var(--kahla-charcoal)',
                borderRadius: '2px'
              }}></div>
              <span style={{ fontSize: '12px' }}>{t.orientation_portrait || 'Portrait'}</span>
            </button>
            <button
              onClick={() => setOrientation('landscape')}
              style={{ 
                flex: 1,
                padding: '16px',
                border: orientation === 'landscape' 
                  ? '2px solid var(--kahla-charcoal)' 
                  : '1px solid var(--kahla-border)',
                backgroundColor: orientation === 'landscape' 
                  ? 'var(--kahla-cream-light)' 
                  : 'var(--kahla-white)',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease'
              }}
            >
              <div style={{ 
                width: '32px', 
                height: '24px', 
                border: '2px solid var(--kahla-charcoal)',
                borderRadius: '2px'
              }}></div>
              <span style={{ fontSize: '12px' }}>{t.orientation_landscape || 'Landscape'}</span>
            </button>
          </div>
        </div>

        {/* Display Toggles */}
        <div style={{ marginBottom: '28px' }}>
          <label style={{ 
            display: 'block',
            fontSize: '11px',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'var(--kahla-text-light)',
            marginBottom: '12px'
          }}>
            {t.include_in_export || 'Include in Export'}
          </label>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '4px',
            backgroundColor: 'var(--kahla-cream-light)',
            padding: '12px 16px',
            border: '1px solid var(--kahla-border)'
          }}>
            <label style={{ 
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              fontSize: '13px',
              padding: '8px 0'
            }}>
              <input 
                type="checkbox"
                checked={showComments}
                onChange={(e) => setShowComments(e.target.checked)}
                style={{ marginRight: '10px' }}
              />
              {t.include_client_notes || 'Client notes / comments under images'}
            </label>
            <label style={{ 
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              fontSize: '13px',
              padding: '8px 0',
              borderTop: '1px solid var(--kahla-border)'
            }}>
              <input 
                type="checkbox"
                checked={showReactions}
                onChange={(e) => setShowReactions(e.target.checked)}
                style={{ marginRight: '10px' }}
              />
              {t.include_reactions_icons || 'Reaction icons under each image (❤️ / 🤍)'}
            </label>
            <label style={{ 
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              fontSize: '13px',
              padding: '8px 0',
              borderTop: '1px solid var(--kahla-border)'
            }}>
              <input 
                type="checkbox"
                checked={showMetadata}
                onChange={(e) => setShowMetadata(e.target.checked)}
                style={{ marginRight: '10px' }}
              />
              {t.include_image_metadata || 'Image metadata (Pose / Light / Wardrobe)'}
            </label>
            <label style={{ 
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              fontSize: '13px',
              padding: '8px 0',
              borderTop: '1px solid var(--kahla-border)'
            }}>
              <input 
                type="checkbox"
                checked={includeDocuments}
                onChange={(e) => setIncludeDocuments(e.target.checked)}
                style={{ marginRight: '10px' }}
                data-testid="export-include-documents"
              />
              {t.include_document_confirmations || 'Document confirmations & timestamps'}
            </label>
            <label style={{ 
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              fontSize: '13px',
              padding: '8px 0',
              borderTop: '1px solid var(--kahla-border)'
            }}>
              <input 
                type="checkbox"
                checked={includeNotes}
                onChange={(e) => setIncludeNotes(e.target.checked)}
                style={{ marginRight: '10px' }}
                data-testid="export-include-notes"
              />
              {t.include_notes_thoughts || 'Notes & Thoughts'}
            </label>
            <label style={{ 
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              fontSize: '13px',
              padding: '8px 0',
              borderTop: '1px solid var(--kahla-border)'
            }}>
              <input 
                type="checkbox"
                checked={includeFinalSelections}
                onChange={(e) => setIncludeFinalSelections(e.target.checked)}
                style={{ marginRight: '10px' }}
                data-testid="export-include-final"
              />
              {t.include_final_selections_label || 'Final Selections section'}
            </label>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn-secondary" style={{ flex: 1 }} onClick={onClose}>
            {t.cancel || 'Cancel'}
          </button>
          <button className="btn-primary" style={{ flex: 1 }} onClick={handleExport}>
            {t.export_pdf || 'Export PDF'}
          </button>
        </div>
      </div>
    </>
  );
};

export default ExportPDFModal;
